<script lang="ts" setup>
import type { MfaEnrolment } from '~kui/types/auth';
import type { ResponseResult } from '~kui/types/formResult';

import { Alert, AlertDescription } from '~ds/components/ui/alert';
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import KUIUnifiedAuthMfaExpired from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthMfaExpired.vue';
import KUIUnifiedAuthMfaSignOut from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthMfaSignOut.vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  loading: boolean;
  expired?: boolean;
  signedIn?: boolean;
  enrolment?: MfaEnrolment | null;
  loadFailed?: boolean;
  recoveryCodes?: string[] | null;
}

const props = withDefaults(defineProps<Props>(), {
  expired: false,
  signedIn: false,
  enrolment: null,
  loadFailed: false,
  recoveryCodes: null,
});

const emit = defineEmits<{
  retry: [];
  submitCode: [code: string];
  saved: [];
  startAgain: [];
  signOut: [];
}>();

const { t } = useI18n();

const RECOVERY_CODES_FILENAME = 'anyway-recovery-codes.txt';

const formResult = ref<ResponseResult | null>(null);
const keyCopied = ref(false);
const codesCopied = ref(false);
const codesCopyFailed = ref(false);
const mounted = ref(false);

onMounted(() => {
  mounted.value = true;
});

const qrCodeSrc = computed(() => {
  if (!props.enrolment) {
    return null;
  }

  const binary = Array.from(new TextEncoder().encode(props.enrolment.qrCodeSvg), byte => String.fromCharCode(byte)).join('');
  return `data:image/svg+xml;base64,${btoa(binary)}`;
});

const codesText = computed(() => `${(props.recoveryCodes ?? []).join('\n')}\n`);

const formConfig = defineFormConfig(
  {
    code: defineField({
      type: 'pincode',
      options: {
        required: true,
        label: t('kui.auth.mfaChallenge.codeLabel'),
        nextFocusOnSubmitButton: true,
      },
      initialValue: '',
      schema: z.string().length(6, t('general.formFields.otpCode.tooShort')).regex(/^\d+$/, t('general.formFields.otpCode.onlyNumbers')),
    }),
  },
  t('kui.auth.mfaEnrol.submit'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  formResult.value = null;
  emit('submitCode', value.code);
};

const copyKey = async () => {
  if (!props.enrolment) {
    return;
  }

  try {
    await navigator.clipboard.writeText(props.enrolment.secret);
    keyCopied.value = true;
  }
  catch {
    keyCopied.value = false;
  }
};

const copyCodes = async () => {
  try {
    await navigator.clipboard.writeText(codesText.value);
    codesCopied.value = true;
    codesCopyFailed.value = false;
  }
  catch {
    codesCopyFailed.value = true;
  }
};

const downloadCodes = () => {
  const url = URL.createObjectURL(new Blob([codesText.value], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = RECOVERY_CODES_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const setError = (message: string) => {
  formResult.value = { success: false, message };
};

defineExpose({ setError });
</script>

<template>
  <div
    class="flex flex-col items-center"
    data-testid="auth-mfa-enrol-step"
  >
    <Heading
      size="xl"
      as="h1"
      class="text-center mb-3"
    >
      {{ t('kui.auth.mfaEnrol.title') }}
    </Heading>
    <Text size="lg" class="text-center">
      {{ t('kui.auth.mfaEnrol.description') }}
    </Text>

    <KUIUnifiedAuthMfaExpired
      v-if="expired"
      class="mt-6"
      @start-again="emit('startAgain')"
    />

    <div v-else-if="loadFailed" class="mt-6 flex flex-col items-center gap-4">
      <Text class="text-center text-destructive" role="alert">
        {{ t('kui.auth.mfaEnrol.loadFailed') }}
      </Text>
      <Button
        type="button"
        data-testid="auth-mfa-enrol-retry"
        @click="emit('retry')"
      >
        {{ t('kui.auth.mfaEnrol.retry') }}
      </Button>
    </div>

    <div
      v-else-if="recoveryCodes"
      class="mt-6 w-full space-y-4"
      data-testid="auth-mfa-recovery-codes"
    >
      <Heading
        as="h2"
        size="lg"
        class="text-center"
      >
        {{ t('kui.auth.mfaRecoveryCodes.title') }}
      </Heading>
      <Alert variant="warning">
        <AlertDescription>{{ t('kui.auth.mfaRecoveryCodes.warning') }}</AlertDescription>
      </Alert>
      <ul
        class="border-border bg-muted text-foreground grid grid-cols-2 gap-2 rounded border p-3 font-mono text-xs"
        data-testid="auth-mfa-recovery-codes-list"
      >
        <li v-for="code in recoveryCodes" :key="code">
          {{ code }}
        </li>
      </ul>
      <div class="flex flex-wrap gap-2">
        <Button
          variant="outline"
          type="button"
          data-testid="auth-mfa-recovery-codes-copy"
          @click="copyCodes"
        >
          <Icon :name="ICONS.copy" />
          {{ codesCopied ? t('kui.auth.mfaRecoveryCodes.copied') : t('kui.auth.mfaRecoveryCodes.copy') }}
        </Button>
        <Button
          variant="outline"
          type="button"
          data-testid="auth-mfa-recovery-codes-download"
          @click="downloadCodes"
        >
          <Icon :name="ICONS.download" />
          {{ t('kui.auth.mfaRecoveryCodes.download') }}
        </Button>
      </div>
      <Text
        v-if="codesCopyFailed"
        size="sm"
        class="text-destructive"
        role="alert"
      >
        {{ t('kui.auth.mfaRecoveryCodes.copyFailed') }}
      </Text>
      <Button
        type="button"
        class="w-full"
        :disabled="loading || !mounted || !recoveryCodes.length"
        data-testid="auth-mfa-recovery-codes-saved"
        @click="emit('saved')"
      >
        {{ t('kui.auth.mfaRecoveryCodes.saved') }}
      </Button>
    </div>

    <div v-else-if="enrolment" class="mt-6 w-full space-y-4">
      <Text class="text-center">
        {{ t('kui.auth.mfaEnrol.scanStep') }}
      </Text>
      <img
        v-if="qrCodeSrc"
        :src="qrCodeSrc"
        :alt="t('kui.auth.mfaEnrol.qrCodeAlt')"
        class="mx-auto size-48"
        data-testid="auth-mfa-enrol-qr"
      >
      <div>
        <Text
          size="sm"
          weight="medium"
          class="mb-2"
        >
          {{ t('kui.auth.mfaEnrol.setupKeyLabel') }}
        </Text>
        <div class="flex items-center gap-2">
          <code
            class="border-border bg-muted text-foreground block min-w-0 flex-1 break-all rounded border p-3 font-mono text-xs"
            data-testid="auth-mfa-enrol-secret"
          >{{ enrolment.secret }}</code>
          <Button
            variant="outline"
            type="button"
            data-testid="auth-mfa-enrol-copy-secret"
            @click="copyKey"
          >
            <Icon :name="ICONS.copy" />
            {{ keyCopied ? t('kui.auth.mfaEnrol.copied') : t('kui.auth.mfaEnrol.copy') }}
          </Button>
        </div>
      </div>
      <Text class="text-center">
        {{ t('kui.auth.mfaEnrol.codeStep') }}
      </Text>
      <KUICoreForm
        class="w-full text-center"
        :form-config="formConfig"
        :form-result="formResult"
        :loading="loading"
        submit-button-size="lg"
        data-testid="auth-mfa-enrol-form"
        @submit="handleSubmit"
      />
    </div>

    <div
      v-else
      class="flex justify-center py-8"
      data-testid="auth-mfa-enrol-loading"
    >
      <KUICoreLoaderAnyway />
    </div>

    <KUIUnifiedAuthMfaSignOut
      v-if="signedIn && !recoveryCodes"
      class="mt-4"
      :disabled="loading"
      @sign-out="emit('signOut')"
    />
  </div>
</template>
